import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE } from './api-constants';
import {
  Applicant, ApplicantRequest, Requirement, RequirementRequest, NewRequirementData,
  ConfigScholarship, MonthlyPayment, CostSheetRequest, CostSheet,
  ApprovedPercentage, ApprovedPercentageRequest, Comment, CommentRequest,
  PedagogicOpinion, ChangeStatusRequest, ResumeCalcResult,
  Institution, Unit, EducationLevel, CivilStatus, PropertyType, DocumentType,
  RequirementDocumentStatus,
  RequirementChange,
  RequirementEditData,
  RequirementReportSummary,
  RequirementYearReport,
  TotvsImportData,
  Student, StudentRequest, MediaItem, Vehicle, PropertyItem, Investment,
  RecreationCenter, BankAccount, CreditCard, Travel, Support,
  Message, TermAcceptance, TermPreview, Setting,
} from '../../models/scholarship.model';

@Injectable({ providedIn: 'root' })
export class ScholarshipApiService {
  constructor(private http: HttpClient) {}

  downloadMedia(id: number): Observable<Blob> {
    return this.http.get(`${API_BASE}/media/${id}/download`, { responseType: 'blob' });
  }

  getProfile(): Observable<Applicant> {
    return this.http.get<Applicant>(`${API_BASE}/profile`);
  }

  updateProfile(data: Partial<Applicant>): Observable<Applicant> {
    return this.http.put<Applicant>(`${API_BASE}/profile`, data);
  }

  getApplicant(id: number, institutionId: number): Observable<Applicant> {
    return this.http.get<Applicant>(`${API_BASE}/institutions/${institutionId}/applicants/${id}`);
  }

  getInstitutions(): Observable<Institution[]> {
    return this.http.get<Institution[]>(`${API_BASE}/institutions`);
  }

  getActiveInstitutions(): Observable<Institution[]> {
    return this.http.get<Institution[]>(`${API_BASE}/institutions/active`);
  }

  createInstitution(data: Partial<Institution>): Observable<Institution> {
    return this.http.post<Institution>(`${API_BASE}/institutions`, data);
  }

  getUnits(institutionId: number): Observable<Unit[]> {
    return this.http.get<Unit[]>(`${API_BASE}/institutions/${institutionId}/units`);
  }

  getEducationLevels(): Observable<EducationLevel[]> {
    return this.http.get<EducationLevel[]>(`${API_BASE}/education-levels`);
  }

  getPropertyTypes(): Observable<PropertyType[]> {
    return this.http.get<PropertyType[]>(`${API_BASE}/property-types`);
  }

  getCivilStatuses(): Observable<CivilStatus[]> {
    return this.http.get<CivilStatus[]>(`${API_BASE}/civil-statuses`);
  }

  getDocumentTypes(): Observable<DocumentType[]> {
    return this.http.get<DocumentType[]>(`${API_BASE}/document-types/options`);
  }

  getConfigScholarships(institutionId: number): Observable<ConfigScholarship[]> {
    return this.http.get<ConfigScholarship[]>(`${API_BASE}/config-scholarships`);
  }

  getConfigScholarshipByYear(institutionId: number, year: number): Observable<ConfigScholarship> {
    return this.http.get<ConfigScholarship>(`${API_BASE}/config-scholarships/by-year`, { params: { year: year.toString() } });
  }

  createConfigScholarship(institutionId: number, data: Partial<ConfigScholarship>): Observable<ConfigScholarship> {
    const payload = { ...data, institution: { id: institutionId } };
    return this.http.post<ConfigScholarship>(`${API_BASE}/config-scholarships`, payload);
  }

  updateConfigScholarship(institutionId: number, id: number, data: Partial<ConfigScholarship>): Observable<ConfigScholarship> {
    const payload = { ...data, institution: { id: institutionId } };
    return this.http.put<ConfigScholarship>(`${API_BASE}/config-scholarships/${id}`, payload);
  }

  deleteConfigScholarship(institutionId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/config-scholarships/${id}`);
  }

  getMonthlyPayments(configId: number): Observable<MonthlyPayment[]> {
    return this.http.get<MonthlyPayment[]>(`${API_BASE}/config-scholarships/${configId}/monthly-payments`);
  }

  createMonthlyPayment(configId: number, data: any): Observable<MonthlyPayment> {
    return this.http.post<MonthlyPayment>(`${API_BASE}/config-scholarships/${configId}/monthly-payments`, data);
  }

  updateMonthlyPayment(configId: number, id: number, data: any): Observable<MonthlyPayment> {
    return this.http.put<MonthlyPayment>(`${API_BASE}/config-scholarships/${configId}/monthly-payments/${id}`, data);
  }

  deleteMonthlyPayment(configId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/config-scholarships/${configId}/monthly-payments/${id}`);
  }

  getNewRequirementData(institutionId: number): Observable<NewRequirementData> {
    return this.http.get<NewRequirementData>(`${API_BASE}/institutions/${institutionId}/requirements/new`);
  }

  getRequirements(institutionId: number, params?: any): Observable<Requirement[]> {
    return this.http.get<Requirement[]>(`${API_BASE}/institutions/${institutionId}/requirements`, { params });
  }

  getRequirement(institutionId: number, id: number): Observable<RequirementEditData> {
    return this.http.get<RequirementEditData>(`${API_BASE}/institutions/${institutionId}/requirements/${id}`);
  }

  createRequirement(institutionId: number, data: RequirementRequest): Observable<Requirement> {
    return this.http.post<Requirement>(`${API_BASE}/institutions/${institutionId}/requirements`, data);
  }

  updateRequirement(institutionId: number, id: number, data: RequirementRequest): Observable<Requirement> {
    return this.http.put<Requirement>(`${API_BASE}/institutions/${institutionId}/requirements/${id}`, data);
  }

  sendRequirement(institutionId: number, id: number, data?: RequirementRequest): Observable<Requirement> {
    return this.http.put<Requirement>(`${API_BASE}/institutions/${institutionId}/requirements/${id}/send`, data ?? {});
  }

  deleteRequirement(institutionId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/institutions/${institutionId}/requirements/${id}`);
  }

  changeRequirementStatus(institutionId: number, id: number, data: ChangeStatusRequest): Observable<Requirement> {
    return this.http.put<Requirement>(`${API_BASE}/institutions/${institutionId}/requirements/${id}/status`, data);
  }

  updateIncomeArbitrated(institutionId: number, id: number, data: { incomeArbitrated: number; incomeArbitratedDescription: string }): Observable<Requirement> {
    return this.http.put<Requirement>(`${API_BASE}/institutions/${institutionId}/requirements/${id}/income-arbitrated`, data);
  }

  updateApplicantDebt(institutionId: number, id: number, hasDebt: boolean): Observable<Requirement> {
    return this.http.put<Requirement>(`${API_BASE}/institutions/${institutionId}/requirements/${id}/applicant-debt`, { applicantDebt: hasDebt });
  }

  updateRequirementAnalyst(institutionId: number, id: number, analystId: number | null): Observable<Requirement> {
    return this.http.put<Requirement>(`${API_BASE}/institutions/${institutionId}/requirements/${id}/analyst`, { analystId });
  }

  getResumeCalc(institutionId: number, id: number): Observable<ResumeCalcResult> {
    return this.http.get<ResumeCalcResult>(`${API_BASE}/institutions/${institutionId}/requirements/${id}/resume-calc`);
  }

  getCostSheet(requirementId: number): Observable<CostSheet> {
    return this.http.get<CostSheet>(`${API_BASE}/requirements/${requirementId}/cost-sheet`);
  }

  updateCostSheet(requirementId: number, data: CostSheetRequest): Observable<CostSheet> {
    return this.http.put<CostSheet>(`${API_BASE}/requirements/${requirementId}/cost-sheet`, data);
  }

  getStudents(requirementId: number): Observable<Student[]> {
    return this.http.get<Student[]>(`${API_BASE}/requirements/${requirementId}/students`);
  }

  getApprovedPercentages(requirementId: number): Observable<ApprovedPercentage[]> {
    return this.http.get<ApprovedPercentage[]>(`${API_BASE}/requirements/${requirementId}/approved-percentages`);
  }

  addApprovedPercentage(requirementId: number, data: ApprovedPercentageRequest): Observable<ApprovedPercentage> {
    return this.http.post<ApprovedPercentage>(`${API_BASE}/requirements/${requirementId}/approved-percentages`, data);
  }

  getComments(requirementId: number, type?: string): Observable<Comment[]> {
    const options = type ? { params: { type } } : {};
    return this.http.get<Comment[]>(`${API_BASE}/requirements/${requirementId}/comments`, options);
  }

  addComment(requirementId: number, data: CommentRequest): Observable<Comment> {
    return this.http.post<Comment>(`${API_BASE}/requirements/${requirementId}/comments`, data);
  }

  getPedagogicOpinions(requirementId: number): Observable<PedagogicOpinion[]> {
    return this.http.get<PedagogicOpinion[]>(`${API_BASE}/requirements/${requirementId}/pedagogic-opinions`);
  }

  addPedagogicOpinion(requirementId: number, opinion: string): Observable<PedagogicOpinion> {
    return this.http.post<PedagogicOpinion>(`${API_BASE}/requirements/${requirementId}/pedagogic-opinions`, { opinion });
  }

  attachMedia(formData: FormData): Observable<MediaItem> {
    return this.http.post<MediaItem>(`${API_BASE}/media/attach`, formData);
  }

  getMedia(modelType: string, modelId: number): Observable<MediaItem[]> {
    return this.http.get<MediaItem[]>(`${API_BASE}/media/${modelType}/${modelId}`);
  }

  getRequirementDocuments(institutionId: number, requirementId: number): Observable<MediaItem[]> {
    return this.http.get<MediaItem[]>(`${API_BASE}/institutions/${institutionId}/requirements/${requirementId}/documents`);
  }

  getRequirementRequiredDocumentStatus(institutionId: number, requirementId: number): Observable<RequirementDocumentStatus[]> {
    return this.http.get<RequirementDocumentStatus[]>(`${API_BASE}/institutions/${institutionId}/requirements/${requirementId}/documents/required-status`);
  }

  getRequirementChanges(institutionId: number, requirementId: number): Observable<RequirementChange[]> {
    return this.http.get<RequirementChange[]>(`${API_BASE}/institutions/${institutionId}/requirements/${requirementId}/changes`);
  }

  uploadRequirementDocument(institutionId: number, requirementId: number, file: File, documentTypeId?: number | null): Observable<MediaItem> {
    const formData = new FormData();
    formData.append('file', file);
    if (documentTypeId) {
      formData.append('documentTypeId', String(documentTypeId));
    }
    return this.http.post<MediaItem>(`${API_BASE}/institutions/${institutionId}/requirements/${requirementId}/documents`, formData);
  }

  uploadRequirementDocumentWithProgress(institutionId: number, requirementId: number, file: File, documentTypeId?: number | null): Observable<HttpEvent<MediaItem>> {
    const formData = new FormData();
    formData.append('file', file);
    if (documentTypeId) {
      formData.append('documentTypeId', String(documentTypeId));
    }
    return this.http.post<MediaItem>(`${API_BASE}/institutions/${institutionId}/requirements/${requirementId}/documents`, formData, {
      observe: 'events',
      reportProgress: true,
    });
  }

  deleteMedia(id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/media/${id}`);
  }

  getComplementaryVehicles(requirementId: number): Observable<Vehicle[]> {
    return this.http.get<Vehicle[]>(`${API_BASE}/requirements/${requirementId}/complementary/vehicles`);
  }

  addComplementaryVehicle(requirementId: number, data: Partial<Vehicle>): Observable<Vehicle> {
    return this.http.post<Vehicle>(`${API_BASE}/requirements/${requirementId}/complementary/vehicles`, data);
  }

  deleteComplementaryVehicle(requirementId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/requirements/${requirementId}/complementary/vehicles/${id}`);
  }

  getComplementaryProperties(requirementId: number): Observable<PropertyItem[]> {
    return this.http.get<PropertyItem[]>(`${API_BASE}/requirements/${requirementId}/complementary/properties`);
  }

  addComplementaryProperty(requirementId: number, data: Partial<PropertyItem>): Observable<PropertyItem> {
    return this.http.post<PropertyItem>(`${API_BASE}/requirements/${requirementId}/complementary/properties`, data);
  }

  deleteComplementaryProperty(requirementId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/requirements/${requirementId}/complementary/properties/${id}`);
  }

  getComplementaryInvestments(requirementId: number): Observable<Investment[]> {
    return this.http.get<Investment[]>(`${API_BASE}/requirements/${requirementId}/complementary/investments`);
  }

  addComplementaryInvestment(requirementId: number, data: Partial<Investment>): Observable<Investment> {
    return this.http.post<Investment>(`${API_BASE}/requirements/${requirementId}/complementary/investments`, data);
  }

  deleteComplementaryInvestment(requirementId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/requirements/${requirementId}/complementary/investments/${id}`);
  }

  getComplementaryRecreationCenters(requirementId: number): Observable<RecreationCenter[]> {
    return this.http.get<RecreationCenter[]>(`${API_BASE}/requirements/${requirementId}/complementary/recreation-centers`);
  }

  addComplementaryRecreationCenter(requirementId: number, data: Partial<RecreationCenter>): Observable<RecreationCenter> {
    return this.http.post<RecreationCenter>(`${API_BASE}/requirements/${requirementId}/complementary/recreation-centers`, data);
  }

  deleteComplementaryRecreationCenter(requirementId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/requirements/${requirementId}/complementary/recreation-centers/${id}`);
  }

  getComplementaryBankAccounts(requirementId: number): Observable<BankAccount[]> {
    return this.http.get<BankAccount[]>(`${API_BASE}/requirements/${requirementId}/complementary/bank-accounts`);
  }

  addComplementaryBankAccount(requirementId: number, data: Partial<BankAccount>): Observable<BankAccount> {
    return this.http.post<BankAccount>(`${API_BASE}/requirements/${requirementId}/complementary/bank-accounts`, data);
  }

  deleteComplementaryBankAccount(requirementId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/requirements/${requirementId}/complementary/bank-accounts/${id}`);
  }

  getComplementaryCreditCards(requirementId: number): Observable<CreditCard[]> {
    return this.http.get<CreditCard[]>(`${API_BASE}/requirements/${requirementId}/complementary/credit-cards`);
  }

  addComplementaryCreditCard(requirementId: number, data: Partial<CreditCard>): Observable<CreditCard> {
    return this.http.post<CreditCard>(`${API_BASE}/requirements/${requirementId}/complementary/credit-cards`, data);
  }

  deleteComplementaryCreditCard(requirementId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/requirements/${requirementId}/complementary/credit-cards/${id}`);
  }

  getComplementaryTravels(requirementId: number): Observable<Travel[]> {
    return this.http.get<Travel[]>(`${API_BASE}/requirements/${requirementId}/complementary/travels`);
  }

  addComplementaryTravel(requirementId: number, data: Partial<Travel>): Observable<Travel> {
    return this.http.post<Travel>(`${API_BASE}/requirements/${requirementId}/complementary/travels`, data);
  }

  deleteComplementaryTravel(requirementId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/requirements/${requirementId}/complementary/travels/${id}`);
  }

  getComplementarySupports(requirementId: number): Observable<Support[]> {
    return this.http.get<Support[]>(`${API_BASE}/requirements/${requirementId}/complementary/supports`);
  }

  addComplementarySupport(requirementId: number, data: Partial<Support>): Observable<Support> {
    return this.http.post<Support>(`${API_BASE}/requirements/${requirementId}/complementary/supports`, data);
  }

  deleteComplementarySupport(requirementId: number, id: number): Observable<void> {
    return this.http.delete<void>(`${API_BASE}/requirements/${requirementId}/complementary/supports/${id}`);
  }

  // ── Messages ────────────────────────────────────────────────────────

  getUnreadCount(institutionId: number): Observable<number> {
    return this.http.get<number>(`${API_BASE}/messages/unread-count`, { params: { institutionId: institutionId.toString() } });
  }

  getMessages(requirementId: number): Observable<Message[]> {
    return this.http.get<Message[]>(`${API_BASE}/messages/requirement/${requirementId}`);
  }

  sendMessage(requirementId: number, content: string): Observable<Message> {
    return this.http.post<Message>(`${API_BASE}/messages/requirement/${requirementId}`, { content });
  }

  markMessageRead(id: number): Observable<void> {
    return this.http.put<void>(`${API_BASE}/messages/${id}/read`, {});
  }

  // ── Terms ───────────────────────────────────────────────────────────

  getTermAcceptance(requirementId: number): Observable<TermAcceptance> {
    return this.http.get<TermAcceptance>(`${API_BASE}/terms/requirement/${requirementId}`);
  }

  getTermPreview(requirementId: number): Observable<TermPreview> {
    return this.http.get<TermPreview>(`${API_BASE}/terms/requirement/${requirementId}/preview`);
  }

  acceptTerm(requirementId: number): Observable<TermAcceptance> {
    return this.http.post<TermAcceptance>(`${API_BASE}/terms/requirement/${requirementId}/accept`, {});
  }

  refuseTerm(requirementId: number): Observable<TermAcceptance> {
    return this.http.post<TermAcceptance>(`${API_BASE}/terms/requirement/${requirementId}/refuse`, {});
  }

  // ── Reports ─────────────────────────────────────────────────────────

  getReportsSummary(institutionId: number): Observable<RequirementReportSummary> {
    return this.http.get<RequirementReportSummary>(`${API_BASE}/institutions/${institutionId}/reports/requirement-group`);
  }

  getReportsDashboard(year?: number): Observable<RequirementReportSummary> {
    const params: Record<string, string> = {};
    if (year) {
      params['year'] = year.toString();
    }
    return this.http.get<RequirementReportSummary>(`${API_BASE}/reports/requirements/dashboard`, { params });
  }

  getReportsByYear(year: number, institutionId: number): Observable<RequirementYearReport> {
    return this.http.get<RequirementYearReport>(`${API_BASE}/institutions/${institutionId}/reports/requirement-year`, { params: { year: year.toString() } });
  }

  exportRequirements(
    institutionId: number,
    format: string,
    year?: number,
    columns?: { name: string; alias: string; format?: string | null }[]
  ): Observable<Blob> {
    const params: Record<string, string> = {
      institutionId: institutionId.toString(),
      format,
    };
    if (year) {
      params['year'] = year.toString();
    }
    if (columns?.length) {
      params['columns'] = JSON.stringify(columns);
    }

    return this.http.get(`${API_BASE}/reports/requirements/export`, {
      params,
      responseType: 'blob',
    });
  }

  downloadRequirementPdf(requirementId: number): Observable<Blob> {
    return this.http.get(`${API_BASE}/reports/requirements/${requirementId}/pdf`, {
      responseType: 'blob',
    });
  }

  getTotvsImportData(requirementId: number): Observable<TotvsImportData> {
    return this.http.get<TotvsImportData>(`${API_BASE}/totvs/requirements/${requirementId}/import`);
  }

  updateTotvsDebt(requirementId: number): Observable<TotvsImportData> {
    return this.http.post<TotvsImportData>(`${API_BASE}/totvs/requirements/${requirementId}/update-debt`, {});
  }

  downloadTotvsReportPdf(requirementId: number): Observable<Blob> {
    return this.http.get(`${API_BASE}/totvs/requirements/${requirementId}/report.pdf`, {
      responseType: 'blob',
    });
  }

  // ── Settings ────────────────────────────────────────────────────────

  getSettings(): Observable<Setting[]> {
    return this.http.get<Setting[]>(`${API_BASE}/settings`);
  }

  getUploadLimit(): Observable<{value: string}> { return this.http.get<{value: string}>(`${API_BASE}/settings/upload-limit`); }

  getSettingByKey(key: string): Observable<Setting> {
    return this.http.get<Setting>(`${API_BASE}/settings/${key}`);
  }

  createSetting(data: Partial<Setting>): Observable<Setting> {
    return this.http.post<Setting>(`${API_BASE}/settings`, data);
  }

  updateSetting(id: number, data: Partial<Setting>): Observable<Setting> {
    return this.http.put<Setting>(`${API_BASE}/settings/${id}`, data);
  }
}
